import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useLang } from '../i18n';
import { APP_SCROLLER_ID, appScroller } from '../lib/app-scroller';
import { BottomNav } from './BottomNav';
import { PullToRefresh } from './PullToRefresh';

export function Layout() {
  const { pathname } = useLocation();
  // Screens that read translations outside React state re-render with the new language.
  const lang = useLang((s) => s.lang);
  // Each screen starts at the top.
  useEffect(() => {
    appScroller()?.scrollTo(0, 0);
  }, [pathname]);
  // A tapped push notification asks the already-open app to show its page.
  const navigate = useNavigate();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (e: MessageEvent<{ type?: string; url?: string }>) => {
      if (e.data?.type !== 'rw:navigate' || !e.data.url) return;
      const url = new URL(e.data.url, location.origin);
      if (url.origin === location.origin) navigate(url.pathname + url.search);
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);
  return (
    <>
      {/* Screens scroll inside this box, never the document: iOS home-screen apps misplace fixed
          elements (the bottom nav ended up mid-screen) once the document itself scrolls, especially
          after the keyboard closes. Absolute, not fixed: a fixed box is its own stacking context and
          would trap the run screen and sheets under the nav. */}
      <div id={APP_SCROLLER_ID} className="absolute inset-0 overflow-y-auto overscroll-y-contain">
        <Outlet key={lang} />
      </div>
      {/* Content scrolling under the translucent status bar stays readable. */}
      <div className="pointer-events-none fixed inset-x-0 top-0 z-30 h-[var(--safe-top)] bg-ink-900/90 backdrop-blur" />
      <PullToRefresh />
      <BottomNav />
    </>
  );
}

export function Page({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <main className={`mx-auto min-h-full max-w-lg px-4 pt-[calc(var(--safe-top)+16px)] pb-nav ${className}`}>{children}</main>;
}
